<script setup>
import { twMerge } from "tailwind-merge";
import { onMounted, onBeforeUnmount } from "vue";

import { Stepper, StepperDescription, StepperItem, StepperSeparator, StepperTitle } from "./ui/stepper";

const stepperId = `stepper-${Math.random().toString(36).slice(2, 8)}`;

const props = defineProps({
  takeFocus: {
    type: Boolean,
    default: true,
  },
  steps: {
    type: Array,
    required: true,
  },
});

// I am so sorry about this, but I just don't see another way
onMounted(() => {
  if (!props.takeFocus) {
    return;
  }

  const els = document.querySelectorAll(`#${stepperId} .initialFocus[tabindex='-1']`);
  if (els?.length === 1) {
    els[0].focus();
  } else {
    console.error("[Accessibility] Invalid initial focus point. Multiple initial focus classes.");
  }

  const target = document.querySelector(`#${stepperId}`);

  const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      if (mutation.type === "attributes" && mutation.attributeName === "class") {
        const el = mutation.target;
        if (el.className.includes("toFocus")) {
          el.focus();
        }
      }
    }
  });

  observer.observe(target, { attributes: true, subtree: true });
  onBeforeUnmount(() => observer.disconnect());
});
</script>

<template>
  <Stepper :id="stepperId" :class="twMerge('flex w-[1024px] items-start gap-2', $attrs.class)">
    <StepperItem
      v-for="(step, i) in steps.filter((step) => !step.disabled)"
      v-slot="{ state }"
      :key="step.step"
      class="relative flex w-full flex-col items-center justify-center"
      :step="step.step"
    >
      <StepperSeparator
        v-if="step.step !== steps[steps.length - 1].step"
        class="absolute left-[calc(50%+20px)] right-[calc(-50%+10px)] top-5 block h-0.5 shrink-0 rounded-full bg-muted group-data-[state=completed]:bg-primary"
      />

      <div
        :class="
          twMerge(
            'z-10 inline-flex h-10 w-10 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0',
            state === 'completed' || state === 'active'
              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
              : 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
            state === 'active' && 'ring-2 ring-ring ring-offset-2 ring-offset-background',
          )
        "
      >
        <component :is="step.icon" />
      </div>

      <div class="mt-5 flex flex-col items-center text-center">
        <StepperTitle>
          <span tabindex="-1" class="no-focus-outline" :class="{ toFocus: state === 'active', initialFocus: i === 0 }">
            {{ step.title }}
          </span>
        </StepperTitle>

        <span v-if="state === 'completed'" class="sr-only">Step completed</span>
        <span v-else-if="state === 'active'" class="sr-only">Current step</span>

        <StepperDescription class="sr-only text-xs text-muted-foreground transition md:not-sr-only lg:text-sm">
          {{ step.description }}
        </StepperDescription>
      </div>
    </StepperItem>
  </Stepper>
</template>
